/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import {
    type CalendarDate,
    getLocalTimeZone,
    parseAbsolute,
    today,
} from '@internationalized/date';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import set from 'lodash/set';
import sortBy from 'lodash/sortBy';
import trimEnd from 'lodash/trimEnd';
import type { DateValue } from 'react-aria';
import { localToday } from 'sarsaparilla';

import type {
    CampgroundReleases,
    FacilityRules,
    Rate,
    Release,
    Season,
} from '../../../types/camping';
import { TypeOfUse } from '../../../types/enums';
import type { SortedTrimmedFutureSeasons } from '../../../types/season';
import { DEFAULT_TIMEZONE } from '../constants';
import {
    DEFAULT_RESERVATION_WINDOW_VALUES,
    FCFS_SEASON,
    PEAK_SEASON,
} from '../constants/campground';
import { DATE_FORMAT_LONG_MONTH } from '../constants/dateFormats';
import { GROUP_SITE, SINGLE_SITE } from '../constants/release';
import { countSiteTypes, getReservationWindowValue } from './campground_utils';
import { getCampsiteReleaseSchedule } from './releasesUtils';
import {
    doDateRangesOverlap,
    format,
    isBetweenDates,
    isDateAfter,
    isDateBefore,
    isDateSameOrAfter,
    isDateSameOrBefore,
    isDateValid,
    isSameDay,
    UTCStringToCalendarDate,
    UTCStringToZonedDateTime,
} from './dateUtils';

export const getSeasonByDate = (seasons: Season[], date: CalendarDate) => {
    return seasons.find((season) =>
        isBetweenDates(date, season.season_start, season.season_end)
    );
};

export const getInitialMonthVisible = (seasonList: Season[], timezone?: string) => {
    const initialDate = today(timezone || DEFAULT_TIMEZONE);
    const season = getSeasonByDate(seasonList, localToday);

    if (!season) {
        // seasons should be sorted always
        return seasonList[0]
            ? UTCStringToCalendarDate(seasonList[0].season_start)
            : initialDate;
    }

    return initialDate;
};

export const getNextAvailableDate = ({
    releaseDates,
    isGroupSite,
}: {
    releaseDates: {
        current_release: {
            group_release_time: string;
            release_time: string;
            group_end: string;
            end: string;
        };
        next_release: {
            release_time: string;
        };
    };
    isGroupSite: boolean;
}) => {
    const start = isGroupSite
        ? releaseDates.current_release?.group_release_time
        : releaseDates.current_release?.release_time;
    const end = isGroupSite
        ? releaseDates.current_release?.group_end
        : releaseDates.current_release?.end;

    if (!start || !end) {
        return null;
    }

    if (isDateBefore(UTCStringToCalendarDate(end), UTCStringToCalendarDate(start))) {
        return releaseDates.next_release?.release_time;
    }

    return null;
};

export const getSeasonsByCutOffDate = (seasons: Season[], cutOffDate: CalendarDate) => {
    let trimmedSeasons = seasons;
    if (cutOffDate && isDateAfter(cutOffDate, localToday)) {
        trimmedSeasons = seasons.filter((season) => {
            return isDateSameOrBefore(
                UTCStringToCalendarDate(season.season_start),
                cutOffDate
            );
        });
    }

    return trimmedSeasons;
};

export const sortSeasons = (seasons: Season[]) =>
    sortBy(seasons, (season) => season.season_start);

export const getCurrentAndFutureSeasons = (seasons: Season[]) => {
    const filteredSeason = filter(seasons, (season) => {
        const seasonEnd = UTCStringToCalendarDate(season.season_end);
        return !isDateBefore(seasonEnd, localToday);
    });

    return sortSeasons(filteredSeason);
};

export const getRecentCurrentAndFutureSeasons = (seasons: Season[]) => {
    const today = localToday;

    const filteredSeason = filter(seasons, (season) => {
        const seasonEnd = UTCStringToCalendarDate(season.season_end);
        return !isDateBefore(seasonEnd.add({ months: 1 }), today);
    });

    return sortSeasons(filteredSeason);
};

const getSeasonGapPosition = ({
    seasonGaps = [],
    curFCFSStart = null,
    curOnlineReservableStart = null,
    curReleaseEnd = null,
    isEntireReleaseFCFS = false,
}: {
    seasonGaps?: Array<{ start_date: CalendarDate; end_date: CalendarDate }>;
    curFCFSStart?: DateValue | null;
    curOnlineReservableStart?: DateValue | null;
    curReleaseEnd?: DateValue | null;
    isEntireReleaseFCFS?: boolean;
}): 'FCFS' | 'ONLINE' | null => {
    let seasonGapPosition: 'FCFS' | 'ONLINE' | null = null;
    // if entire release period only cross over FCFS seasons, seasonGapPosition is 'FCFS'
    if (isEntireReleaseFCFS) {
        return 'FCFS';
    }
    // if season gap isn't empty, find out which season the gap falls into (default is null is gap doesn't fall within the current release)
    if (!isEmpty(seasonGaps)) {
        const season = seasonGaps[0];
        const { start_date: gapStartDate, end_date: gapEndDate } = season;

        // if gap start date is in FCFS period
        if (
            curFCFSStart &&
            curOnlineReservableStart &&
            doDateRangesOverlap(
                gapStartDate,
                gapEndDate,
                curFCFSStart,
                curOnlineReservableStart
            )
        ) {
            seasonGapPosition = 'FCFS';
        }
        // else if gap start date is in non-FCFS period
        else if (
            curOnlineReservableStart &&
            curReleaseEnd &&
            doDateRangesOverlap(
                gapStartDate,
                gapEndDate,
                curOnlineReservableStart,
                curReleaseEnd
            )
        ) {
            seasonGapPosition = 'ONLINE';
        }
    }

    return seasonGapPosition;
};

export const getSeason = ({
    seasons,
    startDate,
    endDate,
}: {
    seasons: Season[];
    startDate: DateValue;
    endDate: DateValue;
}) => {
    const sortedSeasons = sortSeasons(seasons);

    return find(sortedSeasons, (season) => {
        const { season_start, season_end } = season;
        const seasonStartDate = UTCStringToCalendarDate(season_start);
        const seasonEndDate = UTCStringToCalendarDate(season_end);
        return doDateRangesOverlap(startDate, endDate, seasonStartDate, seasonEndDate);
    });
};

export const compileCurrentReleaseInfo = ({
    releaseDates,
    facilityRules,
    siteTypes,
    typeOfUse,
    seasons,
    isCampsite = false,
    reservationWindowValue = null,
    reservationWindowUnit = null,
}: {
    releaseDates: CampgroundReleases;
    facilityRules: FacilityRules;
    siteTypes: any;
    typeOfUse: TypeOfUse;
    seasons: Season[];
    isCampsite: boolean;
    reservationWindowValue: any | null;
    reservationWindowUnit?: string | null;
}) => {
    const releaseInfoMap: Record<string, any> = {};

    const reservationWindowValues = cloneDeep(DEFAULT_RESERVATION_WINDOW_VALUES);
    const siteTypeCounts = countSiteTypes(siteTypes);

    /**
     * If siteTypes is EMPTY, it likely means that /search has failed, so we need to preserve both
     * individual and group site reservation window displays just in case.
     * Otherwise if siteTypes is NOT EMPTY, we will conditionally render individual, group, or both sites'
     * reservation windows depending on the type of campsites at the facility
     */
    if (!isEmpty(siteTypes)) {
        if (siteTypeCounts.single === 0) {
            delete (reservationWindowValues as any).reservationWindow;
        }
        if (siteTypeCounts.group === 0) {
            delete (reservationWindowValues as any).groupReservationWindow;
        }
    }

    forEach(reservationWindowValues, (_, siteType) => {
        const isGroupSite = includes(siteType, GROUP_SITE);
        const type = isGroupSite ? GROUP_SITE : SINGLE_SITE;

        let curReleaseDate: Release = {} as Release;
        let nextReleaseDate: Release = {} as Release;

        if (isCampsite && reservationWindowValue !== null) {
            const campsiteRelease = getCampsiteReleaseSchedule(releaseDates, {
                windowValue: reservationWindowValue,
                windowUnit: reservationWindowUnit,
            });

            curReleaseDate = campsiteRelease?.current_release ?? {};
            nextReleaseDate = campsiteRelease?.next_release ?? {};
        } else {
            curReleaseDate = releaseDates?.current_release ?? {};
            nextReleaseDate = releaseDates?.next_release ?? {};
        }

        // guard against undefined release times that would throw parseAbsolute
        const curReleaseStartTime = isGroupSite
            ? curReleaseDate.group_release_time
            : curReleaseDate.release_time;
        if (!curReleaseStartTime) {
            set(releaseInfoMap, `${type}.hasNoCurrentReleases`, true);
            return;
        }

        // using parseAbsolute to include time
        const curReleaseStart = parseAbsolute(curReleaseStartTime, getLocalTimeZone());
        const curReleaseEnd = isGroupSite
            ? UTCStringToCalendarDate(curReleaseDate.group_end)
            : UTCStringToCalendarDate(curReleaseDate.end);
        const nextReleaseStart = isGroupSite
            ? UTCStringToZonedDateTime(nextReleaseDate.group_release_time)
            : UTCStringToZonedDateTime(nextReleaseDate.release_time);
        const nextReleaseEnd = isGroupSite
            ? UTCStringToCalendarDate(nextReleaseDate.group_end)
            : UTCStringToCalendarDate(nextReleaseDate.end);

        // find the current starting season
        const currentSeason = getSeason({
            seasons,
            startDate: curReleaseStart,
            endDate: curReleaseEnd,
        });

        // find the next season
        const nextSeason = getSeason({
            seasons,
            startDate: nextReleaseStart,
            endDate: nextReleaseEnd,
        });

        /* ============================================== */
        /* Set effective date for current starting season */
        /* ============================================== */
        const effectiveDate = currentSeason?.season_effective_date ?? '';
        if (isDateValid(effectiveDate)) {
            set(releaseInfoMap, `${type}.effectiveDate`, effectiveDate);
        }

        // Get the value and unit of the current type of reservation window
        const { value, unit } = getReservationWindowValue(facilityRules, isGroupSite);

        // Skip displaying this category if reservation window value is 0
        if (value === 0) return;

        const groupSiteType =
            typeOfUse.length === 1 && includes(typeOfUse, 'Day') ? 'day use' : 'group';
        const blockReleased = get(facilityRules, 'blockReleaseDay.value', null);

        const releaseRules = {
            campsiteType: isGroupSite ? groupSiteType : 'individual',
            reservationWindow: `${value}-${trimEnd(unit, 's')}`,
            blockReleaseDate: blockReleased ? blockReleased.toString() : '',
        };

        /* ======================================= */
        /* Set if current or next release is valid */
        /* ======================================= */

        // if current release time is after its end time, the data is invalid, therefore there is no current release
        let hasNoCurrentReleases = isDateBefore(curReleaseEnd, curReleaseStart);

        // if next release time is after its end time, the data is invalid, therefore there is no next release
        const isNextReleaseInvalid = isDateBefore(nextReleaseEnd, nextReleaseStart);
        // when next release range is same as current release, this also means there are no future release dates
        const isNextReleaseDuplicate = isSameDay(curReleaseEnd, nextReleaseEnd);
        const hasNoFutureReleases = isNextReleaseInvalid || isNextReleaseDuplicate;

        if (!hasNoCurrentReleases) {
            set(releaseInfoMap, `${type}.curReleaseStart`, curReleaseStart);
            set(releaseInfoMap, `${type}.curReleaseEnd`, curReleaseEnd);
        }

        if (!hasNoFutureReleases) {
            set(releaseInfoMap, `${type}.nextReleaseStart`, nextReleaseStart);
            set(releaseInfoMap, `${type}.nextReleaseEnd`, nextReleaseEnd);
            if (nextSeason?.season_start) {
                set(
                    releaseInfoMap,
                    `${type}.nextSeasonStart`,
                    UTCStringToCalendarDate(nextSeason.season_start)
                );
            }
            if (nextSeason?.season_end) {
                set(
                    releaseInfoMap,
                    `${type}.nextSeasonEnd`,
                    UTCStringToCalendarDate(nextSeason.season_end)
                );
            }
        }

        /* ================== */
        /* Set effective date */
        /* ================== */
        const isCurReleaseEffective =
            !hasNoCurrentReleases &&
            (!effectiveDate ||
                isDateSameOrAfter(
                    curReleaseStart,
                    UTCStringToCalendarDate(effectiveDate)
                ));
        // if current release is not effective, then there is no current release
        if (!isCurReleaseEffective) {
            hasNoCurrentReleases = true;
        }
        set(releaseInfoMap, `${type}.hasNoCurrentReleases`, hasNoCurrentReleases);
        set(releaseInfoMap, `${type}.isCurReleaseEffective`, isCurReleaseEffective);
        set(releaseInfoMap, `${type}.hasNoFutureReleases`, hasNoFutureReleases);
        set(releaseInfoMap, `${type}.curOnlineReservableStart`, curReleaseStart);

        /* ======================== */
        /* Set current season dates */
        /* ======================== */
        releaseInfoMap[type] = { ...releaseInfoMap[type], ...releaseRules };
        const currentSeasonStart = currentSeason?.season_start
            ? UTCStringToCalendarDate(currentSeason.season_start)
            : null;
        let currentSeasonEnd = currentSeason?.season_end
            ? UTCStringToCalendarDate(currentSeason.season_end)
            : null;

        const isStartOfSeasonsFCFS =
            get(currentSeason, 'season_type', PEAK_SEASON) === FCFS_SEASON;
        const isNextSeasonFCFS =
            get(nextSeason, 'season_type', PEAK_SEASON) === FCFS_SEASON;

        if (isStartOfSeasonsFCFS) {
            set(
                releaseInfoMap,
                `${type}.curFCFSStart`,
                currentSeasonStart && isDateBefore(curReleaseStart, currentSeasonStart)
                    ? currentSeasonStart
                    : curReleaseStart
            );
            set(releaseInfoMap, `${type}.curOnlineReservableStart`, null);
        }

        if (currentSeasonStart && currentSeasonEnd) {
            set(releaseInfoMap, `${type}.seasonStart`, currentSeasonStart);
            set(releaseInfoMap, `${type}.seasonEnd`, currentSeasonEnd);
            set(releaseInfoMap, `${type}.isStartOfSeasonsFCFS`, isStartOfSeasonsFCFS);
            set(releaseInfoMap, `${type}.isSeasonSeriesEndFCFS`, isStartOfSeasonsFCFS);
            set(releaseInfoMap, `${type}.isNextSeasonFCFS`, isNextSeasonFCFS);

            const isReleasedSeasonInTheFuture =
                curReleaseStart &&
                isDateSameOrBefore(curReleaseStart, currentSeasonStart);
            set(
                releaseInfoMap,
                `${type}.isReleasedSeasonInTheFuture`,
                isReleasedSeasonInTheFuture
            );

            /* if the current release end is AFTER the current pending season end,
            begin appending the next effective season and so on; as long as the
            iterative season is effective */
            let i = 0;

            /* Season is effective if 'season_effective_date' value is....
                (1) not valid by isDateValid() method (e.g. "0001-01-01T00:00:00Z")
                (2) valid, but is in the past
            */
            const isSeasonEffective =
                !isDateValid(seasons[i].season_effective_date) ||
                (isDateValid(seasons[i].season_effective_date) &&
                    isDateSameOrAfter(
                        localToday,
                        UTCStringToCalendarDate(seasons[i].season_effective_date)
                    ));
            const seasonGaps = [];
            const furthestReservableDate = localToday.add({ [unit]: value });
            /* while current release end date is still after the current iterated season end date,
            continue checking the next consecutive season */
            while (
                currentSeasonEnd &&
                isDateAfter(curReleaseEnd, currentSeasonEnd) &&
                isSeasonEffective &&
                i < seasons.length
            ) {
                currentSeasonEnd = UTCStringToCalendarDate(seasons[i].season_end); // get next season
                const currentSeasonType = seasons[i].season_type;
                // else if season is valid and effective, append season
                /* if this seasons iteration changed from FCFS season to non-FCFS for the first time, mark this pivot,
                so we can show users that the released period is part FCFS and part online-reservable */
                if (
                    isStartOfSeasonsFCFS &&
                    currentSeasonType !== FCFS_SEASON &&
                    !get(releaseInfoMap, `${type}.curOnlineReservableStart`, null)
                ) {
                    set(
                        releaseInfoMap,
                        `${type}.curOnlineReservableStart`,
                        UTCStringToCalendarDate(seasons[i].season_start)
                    );
                    const seasonEffectiveDate = seasons[i].season_effective_date ?? '';
                    const isCurrentSeasonEffective = isDateSameOrAfter(
                        localToday,
                        UTCStringToCalendarDate(seasonEffectiveDate)
                    );

                    if (isDateValid(seasonEffectiveDate)) {
                        set(releaseInfoMap, `${type}.effectiveDate`, seasonEffectiveDate);
                        set(
                            releaseInfoMap,
                            `${type}.isMiddleSeasonEffective`,
                            !isCurrentSeasonEffective
                        );
                    }
                }

                const isPrevSeasonFCFS =
                    seasons[i - 1] && seasons[i - 1].season_type === FCFS_SEASON; // check if previous season is FCFS type
                /* if current iterated season...
                    - is not FCFS season (if a FCFS season follows an online reservable season, we don't need to show it)
                    - or, is FCFS season, but the previous season is also FCFS
                ... modify the existing seasonEnd and releaseEnd */
                if (
                    currentSeasonType !== FCFS_SEASON ||
                    (currentSeasonType === FCFS_SEASON && isPrevSeasonFCFS)
                ) {
                    if (isDateBefore(furthestReservableDate, currentSeasonEnd)) {
                        set(
                            releaseInfoMap,
                            `${type}.furthestReservableDate`,
                            furthestReservableDate
                        );
                    }
                    set(releaseInfoMap, `${type}.seasonEnd`, currentSeasonEnd);

                    /* override existing 'isSeasonSeriesEndFCFS' value based on the season type of this current latest iterated season.
                    We need to record the last reservable season's type, so we show the correct season end bubble icon on the timeline */
                    set(
                        releaseInfoMap,
                        `${type}.isSeasonSeriesEndFCFS`,
                        currentSeasonType === FCFS_SEASON
                    );

                    set(releaseInfoMap, `${type}.curReleaseEnd`, curReleaseEnd);
                }

                // if there is a next season in the current release window, and it is not continuous to the current season, add the gap date range
                if (
                    seasons[i + 1] &&
                    !isSameDay(
                        currentSeasonEnd.add({ days: 1 }),
                        UTCStringToCalendarDate(seasons[i + 1].season_start)
                    )
                ) {
                    seasonGaps.push({
                        start_date: currentSeasonEnd,
                        end_date: UTCStringToCalendarDate(seasons[i + 1].season_start),
                    });
                }

                i += 1;
            }
            set(releaseInfoMap, `${type}.seasonGaps`, seasonGaps);
        }

        /* ============== */
        /* Set walkup day */
        /* ============== */

        // if the starting released season is FCFS season, it is unnecessary to modify the 'online reservable' start date
        if (!isStartOfSeasonsFCFS) {
            let walkupDays;
            let cutOffType;
            let curFCFSStart = null;
            let currentReservableStart;

            // if current released start date is BEFORE the released season...
            if (currentSeasonStart && isDateBefore(curReleaseStart, currentSeasonStart)) {
                // (1) set the current reservable start date to the released season start */
                currentReservableStart = currentSeasonStart;
                // (2) set reservable cutoff window (walkup days) to be 0
                walkupDays = 0;
            }
            // if current released start date is ON OR AFTER the released season...
            else {
                // (1) set the current reservable start date to the release start date from the BE data
                currentReservableStart = curReleaseStart;
                // (2) if set reservable cutoff window (walkup days) based on business rules
                // (reservationCutOff property is not set, the default is 1 day)
                walkupDays = facilityRules?.reservationCutOff?.value ?? 1;
                cutOffType = get(
                    facilityRules,
                    'reservationCutOff.secondary_value',
                    'Not Reservable'
                );
            }

            let curOnlineReservableStart = currentReservableStart;

            /* if FCFS date exists, the FCFS start date will be the current reservable start date,
            and the current online reservable start date would be to pushed back by the num of FCFS dates.
            */
            if (currentReservableStart && walkupDays && walkupDays !== 0) {
                if (cutOffType === 'Not Reservable') {
                    curFCFSStart = currentReservableStart;
                }
                curOnlineReservableStart = currentReservableStart.add({
                    days: walkupDays,
                });
            }

            set(
                releaseInfoMap,
                `${type}.curOnlineReservableStart`,
                curOnlineReservableStart
            );

            if (curFCFSStart) {
                set(releaseInfoMap, `${type}.curFCFSStart`, curFCFSStart);
            }
        }

        /* ================================== */
        /* Set potential season gap position  */
        /* ================================== */
        const {
            seasonGaps,
            curFCFSStart,
            curOnlineReservableStart,
            isStartOfSeasonsFCFS: isStartFCFS,
            isSeasonSeriesEndFCFS: isEndFCFS,
        } = releaseInfoMap[type];

        // after getting solid FCFS and online reservation start dates, determine where the season gap is located at
        if (!isEmpty(seasonGaps)) {
            const seasonGapPosition = getSeasonGapPosition({
                seasonGaps,
                curFCFSStart,
                curOnlineReservableStart,
                curReleaseEnd,
                isEntireReleaseFCFS: isStartFCFS && isEndFCFS,
            });
            set(releaseInfoMap, `${type}.seasonGapsPosition`, seasonGapPosition);
        }
    });
    return releaseInfoMap;
};

export const getSortedTrimmedFutureSeasons = (
    releaseDates: CampgroundReleases,
    facilityRules: FacilityRules,
    siteTypes: string,
    typeOfUse: TypeOfUse,
    seasonData: Season[],
    isCampsite = false,
    reservationWindowValue = null,
    reservationWindowUnit: string | null = null
): SortedTrimmedFutureSeasons => {
    const furthestDisplayableDate = localToday.add({ years: 1 });

    // (1) Filter only seasons that have only expired for less than 1 month, CURRENT, or in the FUTURE (up to 1 year)
    const sortedRecentAndFuture = getRecentCurrentAndFutureSeasons(seasonData);

    // (2) Filter only seasons that are CURRENT or in the FUTURE
    let sortedFutureSeasons;
    if (sortedRecentAndFuture) {
        sortedFutureSeasons = getCurrentAndFutureSeasons(sortedRecentAndFuture);
    }

    // (3) Filter only CURRENT & FUTURE seasons that are applicable to the current and the next release schedules
    if (sortedFutureSeasons && releaseDates && !isEmpty(releaseDates)) {
        const { next_release } = releaseDates;

        // 3a) get trimmed season list that only includes seasons that are valid until the next release period (used for booking timeline)
        /* a facility could have individual and group type sites with different end date release,
        just pick the furthest to use as furthest reservable date */
        const furthestReservableDate = isDateAfter(
            UTCStringToCalendarDate(next_release.group_end),
            UTCStringToCalendarDate(next_release.end)
        )
            ? UTCStringToCalendarDate(next_release.group_end)
            : UTCStringToCalendarDate(next_release.end);

        const sortedTrimmedFutureSeasons = getSeasonsByCutOffDate(
            sortedFutureSeasons,
            furthestReservableDate
        );

        const releaseInfoMap = compileCurrentReleaseInfo({
            releaseDates,
            facilityRules,
            siteTypes,
            typeOfUse,
            seasons: sortedTrimmedFutureSeasons,
            isCampsite,
            reservationWindowValue,
            reservationWindowUnit,
        });

        // 3b) get trimmed season list that includes seasons that are valid until a year from 'today' (used for seasons table)
        const sortedDisplayableFutureSeasons =
            isCampsite === false
                ? getSeasonsByCutOffDate(sortedFutureSeasons, furthestDisplayableDate)
                : null;

        return { releaseInfoMap, sortedDisplayableFutureSeasons };
    }
    return { releaseInfoMap: {}, sortedDisplayableFutureSeasons: null };
};

export const getOutOfSeasonRanges = (seasons: Season[] = []) => {
    const gaps: string[] = [];
    let gapEnd;
    let gapStart: CalendarDate;

    // 1. sort seasons array
    const sortedSeasons = sortBy(seasons, (season) => season.season_start);

    // 2. get gaps between seasons
    sortedSeasons.forEach((season) => {
        // update gapEnd to current season start date
        gapEnd = UTCStringToCalendarDate(season.season_start);

        // if the previous season end date (gapStart) plus 1 day doesn't bridge the current season start (gapEnd), record gap
        // ignore first season (when 'gapStart' is undefined)
        if (gapStart && !isSameDay(gapStart.add({ days: 1 }), gapEnd)) {
            gaps.push(
                `${format(gapStart, DATE_FORMAT_LONG_MONTH)} - ${format(gapEnd, DATE_FORMAT_LONG_MONTH)}`
            );
        }

        // update gapStart to current season end date
        gapStart = UTCStringToCalendarDate(season.season_end);
    });

    return gaps;
};

export const getBookingWindowSelectOptions = (releaseOptions: any) => {
    const bookingWindows = [];

    for (const option of releaseOptions) {
        if (option === SINGLE_SITE) {
            bookingWindows.push('Advance Booking - Individual Sites');
        } else if (option === GROUP_SITE) {
            bookingWindows.push('Advance Booking - Group Sites');
        } else if (option === '0') {
            bookingWindows.push('Release today');
        } else {
            bookingWindows.push(
                `Release ${option} ${option === '1' ? 'Day' : 'Days'} in advance`
            );
        }
    }
    return bookingWindows;
};

export const getMinAndMaxRates = (seasons: Season[]) => {
    let minRate = Infinity;
    let maxRate = 0;
    seasons.forEach((season) => {
        Object.values(season.rate_map).forEach((rate: Rate) => {
            if (rate.per_night < minRate) {
                minRate = rate.per_night;
            }
            if (rate.per_night > maxRate) {
                maxRate = rate.per_night;
            }
        });
    });
    return { minRate: minRate === Infinity ? 0 : minRate, maxRate };
};
